if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this._onSubmitHandler = (evt) => this.onSubmitHandler(evt);
        this._onPointerDownHandler = () => this.handleSubmitPointerDown();
      }

      connectedCallback() {
        if (this._initialized) return;

        this.form = this.querySelector('form');
        if (!this.form) return;

        this._initialized = true;

        const variantInput = this.variantIdInput;
        if (variantInput) variantInput.disabled = false;

        this.form.addEventListener('submit', this._onSubmitHandler);

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.form.querySelector('[type="submit"]');
        if (!this.submitButton) return;

        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.submitButton.addEventListener('pointerdown', this._onPointerDownHandler);

        this.hideErrors = this.dataset.hideErrors === 'true';

        /* ── Bundle Product: flag detection ── */
        this.isBundle = this.dataset.isBundle === 'true';
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this._onSubmitHandler);
        if (this.submitButton) this.submitButton.removeEventListener('pointerdown', this._onPointerDownHandler);
        this._initialized = false;
      }

      refreshSubmitButton() {
        this.submitButton = this.form?.querySelector('[type="submit"]') || this.submitButton;
        return this.submitButton;
      }

      handleSubmitPointerDown() {
        this.refreshSubmitButton();
        if (!this.submitButton || this.submitButton.disabled) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.setLoading(true);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.refreshSubmitButton();
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.setLoading(true);

        /* ── Route to the correct submission path ── */
        if (this.isBundle) {
          this.handleBundleSubmit();
        } else {
          this.handleDefaultSubmit();
        }
      }

      getSubmitSpinner() {
        return (
          this.submitButton?.querySelector(':scope > .loading__spinner') ||
          this.submitButton?.querySelector('.loading__spinner')
        );
      }

      setLoading(isLoading) {
        this.refreshSubmitButton();
        if (!this.submitButton) return;

        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');

        const spinner = this.getSubmitSpinner();
        if (!spinner) return;

        if (isLoading) {
          spinner.classList.remove('hidden');
          spinner.removeAttribute('hidden');
          requestAnimationFrame(() => {
            spinner.classList.remove('hidden');
          });
        } else {
          spinner.classList.add('hidden');
        }
      }

      parseCartAddResponse(response) {
        return response.text().then((text) => {
          let parsed = {};

          if (text) {
            try {
              parsed = JSON.parse(text);
            } catch (error) {
              if (response.status === 429) {
                throw new Error('Too many requests. Please wait a moment and try again.');
              }
              throw new Error('Unable to add to cart. Please try again.');
            }
          }

          if (!response.ok && !parsed.status) {
            parsed.status = response.status;
            parsed.description =
              parsed.description || parsed.message || 'Unable to add to cart. Please try again.';
          }

          return parsed;
        });
      }

      handleFetchError(error) {
        console.error(error);
        this.handleErrorMessage(error.message || 'Unable to add to cart. Please try again.');
        this.error = true;
      }

      getQuickAddModal() {
        return this.closest('quick-add-modal');
      }

      /* ══════════════════════════════════════════════════════════════
       *  DEFAULT (non-bundle) submission — unchanged Dawn logic
       * ══════════════════════════════════════════════════════════════ */
      handleDefaultSubmit() {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => this.parseCartAddResponse(response))
          .then((response) => {
            this.handleCartResponse(response, formData);
          })
          .catch((e) => {
            this.handleFetchError(e);
          })
          .finally(() => {
            this.resetButtonState();
          });
      }

      /* ══════════════════════════════════════════════════════════════
       *  BUNDLE submission — multi-patch items[] payload
       *  Uses JSON content-type and /cart/add.js items array
       * ══════════════════════════════════════════════════════════════ */
      handleBundleSubmit() {
        /* 1. Collect selected patches from the standalone checkbox group */
        const patchCheckboxes = document.querySelectorAll('[data-bundle-patches] input[type="checkbox"]:checked');
        const selectedPatches = Array.from(patchCheckboxes).map((cb) => cb.value);

        if (selectedPatches.length === 0) {
          this.handleErrorMessage('Please select at least one patch style.');
          this.resetButtonState();
          return;
        }

        /* 2. Get the base variant ID (resolved by size only — patches are excluded from variant-selects) */
        const baseVariantId = this.variantIdInput.value;
        if (!baseVariantId) {
          this.handleErrorMessage('Please select a size.');
          this.resetButtonState();
          return;
        }
        const formData = new FormData(this.form);
        const baseQuantity = Number(formData.get('quantity')) || 1;

        const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

        /* 3. Build the items array */
        const items = [
          {
            id: parseInt(baseVariantId),
            quantity: baseQuantity,
            properties: {
              'Selected Patches': selectedPatches.join(', '),
              '_selected_patches': selectedPatches.join(', '),
              '_is_bundle_base': 'true',
              '_bundle_id': bundleId,
            },
          },
        ];

        /* 4. If patches > 2, add the extra patch add-on product */
        const extraPatchVariantId = this.dataset.extraPatchVariant;
        if (selectedPatches.length > 2 && extraPatchVariantId) {
          const extraQuantity = (selectedPatches.length - 2) * baseQuantity;
          items.push({
            id: parseInt(extraPatchVariantId),
            quantity: extraQuantity,
            properties: {
              '_is_bundle_addon': 'true',
              '_associated_base': baseVariantId,
              '_bundle_id': bundleId,
            },
          });
        }

        /* 5. Build JSON request */
        const sectionsToRender = this.cart ? this.cart.getSectionsToRender().map((s) => s.id) : [];
        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        const payload = {
          items: items,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        };

        fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        })
          .then((response) => this.parseCartAddResponse(response))
          .then((response) => {
            /* /cart/add.js with items[] returns { items: [...], sections: {...} }
               We need to normalize this so renderContents works correctly */
            if (response.status) {
              /* Error case */
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: baseVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            /* Success: publish cart update and render cart drawer/notification */
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: baseVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            this.closeQuickAddAndRenderCart(response);
          })
          .catch((e) => {
            this.handleFetchError(e);
          })
          .finally(() => {
            this.resetButtonState();
          });
      }

      /* ── Shared: process cart response (used by default flow) ── */
      handleCartResponse(response, formData) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;
        this.closeQuickAddAndRenderCart(response);
      }

      closeQuickAddAndRenderCart(response) {
        const quickAddModal = this.getQuickAddModal();
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
        }
      }

      /* ── Reset button to idle state ── */
      resetButtonState() {
        this.refreshSubmitButton();
        if (!this.submitButton) return;

        this.setLoading(false);
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
